import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { fadeInUpAnimation } from 'app/app-layout/animations';
import { AuthService } from 'app/core/authentication/services';

// Imported Tinymce config file
import { MceSettings } from 'app/core/mce-config/mce-editor-config.constant';
import { UserInterface } from 'app/core/user/models';
import { DashboardAccordionIndexKey } from 'app/my-zone/home/constants/dashboard-accordion-index-key';
import { DashboardTabIndexKey } from 'app/my-zone/home/constants/dashboard-tab-index-key';
import { NotificationInterface } from 'app/notifications/models';
import { NotificationService } from 'app/notifications/services/notification.service';
import { RecommendationInterface } from 'app/recommendation/models/recommendation.model';
import { RecommendationService } from 'app/recommendation/services/recommendation.service';
import { GlobalConstants } from 'app/shared/constants/globalconstants';
import { SiteSupportedLanguageInterface } from 'app/site-settings/models/site-supported-language.model';
import { MatSnackBar } from '@angular/material';
import { MessageBannerInterface, MessageInterface, MessageTextInterface } from 'app/site-settings/models/message-banner.model';
import { SiteSettingsService } from 'app/site-settings/services/site-settings.service';
import { TaskInterface } from 'app/task/models';
import { TaskService } from 'app/task/services';
// imported cookie service
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MyZoneHelpDialogComponent } from './my-zone-help-dialog/my-zone-help-dialog.component';
import { MyZoneLoginBannerComponent } from './my-zone-login-banner/my-zone-login-banner.component';

@Component({
  selector: 'cz-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInUpAnimation],
})
export class HomeComponent implements OnInit, OnDestroy {
  public selectedTabIndex: number = 0;
  public loggedInUser: any;
  public selectedAccordionIndex: number = 0;
  //  variable declaration and assign
  public tinyConfig: any = MceSettings.default;
  public readonly recommendationsOpenForUser$: BehaviorSubject<Readonly<RecommendationInterface[]>>;
  public readonly recommendationsCompletedForUser$: BehaviorSubject<Readonly<RecommendationInterface[]>>;
  public recommendationBadgeValue: number;

  public readonly tasksCompletedForUser$: BehaviorSubject<Readonly<TaskInterface[]>>;
  public readonly tasksCompletedByUser$: BehaviorSubject<Readonly<TaskInterface[]>>;
  public readonly tasksOpenAndAssignedToUser$: BehaviorSubject<Readonly<TaskInterface[]>>;
  public readonly tasksOpenAndTheSubjectIsUser$: BehaviorSubject<Readonly<TaskInterface[]>>;
  public taskBadgeValue: number;
  public isTaskOverdue: boolean;
  private snackbar: boolean = false;
  public messageBanner: MessageBannerInterface;

  public readonly notificationsForUser$: BehaviorSubject<Readonly<NotificationInterface[]>>;
  public notificationBadgeValue: number;

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  public supportedlanguage$: BehaviorSubject<SiteSupportedLanguageInterface[]>;
  public messageBanner$: BehaviorSubject<MessageBannerInterface>;
  constructor(private readonly _activatedRoute: ActivatedRoute,
              private readonly _taskService: TaskService,
              private readonly _authService: AuthService,
              private readonly _cookieService: CookieService,
              private readonly _recommendationService: RecommendationService,
              private readonly _notificationService: NotificationService,
              private readonly dialog: MatDialog,
              private readonly router: Router,
              private _siteSettingsService: SiteSettingsService,
              private readonly _snackBar: MatSnackBar,
              public _GlobalConstants: GlobalConstants) {

    this.recommendationsOpenForUser$ = new BehaviorSubject<Readonly<RecommendationInterface[]>>(undefined);
    this.recommendationsCompletedForUser$ = new BehaviorSubject<Readonly<RecommendationInterface[]>>(undefined);
    this.recommendationBadgeValue = 0;

    this.tasksCompletedForUser$ = new BehaviorSubject<Readonly<TaskInterface[]>>(undefined);
    this.tasksCompletedByUser$ = new BehaviorSubject<Readonly<TaskInterface[]>>(undefined);
    this.tasksOpenAndAssignedToUser$ = new BehaviorSubject<Readonly<TaskInterface[]>>(undefined);
    this.tasksOpenAndTheSubjectIsUser$ = new BehaviorSubject<Readonly<TaskInterface[]>>(undefined);
    this.taskBadgeValue = 0;

    this.notificationsForUser$ = new BehaviorSubject<Readonly<NotificationInterface[]>>(undefined);
    // Getting culture (cookie) value to update TinyMce localization
    if (this._cookieService.get('Culture') !== 'en') {
      this.tinyConfig.language_url = './assets/langs/' + this._cookieService.get('Culture') + '.js';
    } else {
      this.tinyConfig.language_url = '';
    }
    this.tinyConfig.language = this._cookieService.get('Culture');
  }

  public ngOnInit(): void {
    this._activatedRoute.paramMap.pipe(takeUntil(this.destroy$))
      .subscribe((paramMap: ParamMap) => {
        if (paramMap.has(DashboardTabIndexKey.tabIndex)) {
          this.selectedTabIndex = +paramMap.get(DashboardTabIndexKey.tabIndex);
        }

        if (paramMap.has(DashboardAccordionIndexKey.accordionIndex)) {
          this.selectedAccordionIndex = +paramMap.get(DashboardAccordionIndexKey.accordionIndex);
        }
        if (this._GlobalConstants.backupLanguageId === 0 && this._GlobalConstants.cultureCookieId === 0) {
          this._siteSettingsService.loadSiteSupportedLanguage()
            .pipe(takeUntil(this.destroy$))
            .subscribe((languageData: SiteSupportedLanguageInterface[]) => {
              if (languageData === undefined) { return; }
              languageData.map((language: SiteSupportedLanguageInterface) => {
                if (language.isBackupTranslation) {
                  this._GlobalConstants.backupLanguageId = language.languageId;
                }
                if (language.culture === this._cookieService.get('Culture')) {
                  this._GlobalConstants.cultureCookieId = language.languageId;

                }
              });

            });
            // this._siteSettingsService.loadBanner()
            // .pipe(takeUntil(this.destroy$))
            // .subscribe((messages:MessageBannerInterface) => {
            //   if (!messages) { return; }
            //   let index: number = -1;
            //   for(let i = 0; i < messages.messages.length; i++)
            //     if(1 === messages.messages[i].languageId)
            //       index = i;
            //   const dbData: MessageInterface = messages.messages[index];

            //   if(messages.active)
            //     this.openDialog(dbData);
            // });
        }
      });



    // Load all tasks for the logged in user.
    this._taskService.loadAll();

    // Load all recommendations for the logged in user
    this._recommendationService.loadAll();

    // Load all user notifications
    this._notificationService.loadAll();


    // Get the authenticated user so we can scope the task loads.
    this.onInitLogic();
    // this.getRecommendationEvent();

  }

  public openDialog(messageDefault: MessageInterface): void {
    const dialog = this.dialog.open(MyZoneLoginBannerComponent, {
      data:{
        message: messageDefault
      },
      height: '700px',
      width: '700px',
      panelClass: 'my-zone-login-banner',

    });
    dialog.afterClosed().subscribe(()=>{
      this.dialog.closeAll();
    })
  }

  public onInitLogic() {
    this._authService.getAuthenticatedUser()
      .pipe(
        takeUntil(this.destroy$))
      .subscribe((authenticatedUser: UserInterface) => {
        if (authenticatedUser === undefined) {
          return;
        }
        this.loggedInUser = authenticatedUser;
        this._taskService.findAllTasksCompletedForUserId(authenticatedUser.id)
          .pipe(
            takeUntil(this.destroy$))
          .subscribe((tasks: TaskInterface[]) => {
            this.tasksCompletedForUser$.next(tasks.sort(this.closedTaskComparator));
          });

        this._taskService.findAllTasksCompletedByUserId(authenticatedUser.id)
          .pipe(
            takeUntil(this.destroy$))
          .subscribe((tasks: TaskInterface[]) => {
            this.tasksCompletedByUser$.next(tasks.sort(this.closedTaskComparator));
          });

        this._taskService.findAllOpenTasksAssignedToUserId(authenticatedUser.id)
          .pipe(
            takeUntil(this.destroy$))
          .subscribe((tasks: TaskInterface[]) => {
            this.taskBadgeValue = tasks.length;
            this.isTaskOverdue = tasks.findIndex((task: TaskInterface) => task.isOverDue) >= 0;
            this.tasksOpenAndAssignedToUser$.next(tasks.sort(this.openTaskComparator));
          });

        this._taskService.findAllOpenTasksWhereTheSubjectIsUserId(authenticatedUser.id)
          .pipe(
            takeUntil(this.destroy$))
          .subscribe((tasks: TaskInterface[]) => {
            this.tasksOpenAndTheSubjectIsUser$.next(tasks.sort(this.openTaskComparator));
          });

        this._recommendationService.findAllOpenRecommendationsThatApplyToUserId(authenticatedUser.id)
          .pipe(
            takeUntil(this.destroy$))
          .subscribe((recommendations: RecommendationInterface[]) => {
            this.recommendationBadgeValue = recommendations.length;
            this.recommendationsOpenForUser$.next(recommendations);
          });

        this._recommendationService.findAllRecommendationsCompletedByUserId(authenticatedUser.id)
          .pipe(
            takeUntil(this.destroy$))
          .subscribe((recommendations: RecommendationInterface[]) => {
            this.recommendationsCompletedForUser$.next(recommendations);
          });

        this._notificationService.getNotifications()
          .pipe(
            takeUntil(this.destroy$))
          .subscribe((notifications: NotificationInterface[]) => {
            this.notificationBadgeValue = notifications.filter((notification: NotificationInterface) => !notification.read).length;
            this.notificationsForUser$.next(notifications);
          });

        this.dialog.closeAll();
        this._siteSettingsService.loadTextBanner()
          .pipe(takeUntil(this.destroy$))
          .subscribe((message:MessageTextInterface) => {
            if (!message) { return; }
            if(message.active && localStorage.getItem("messageShown")!=="true" && !!message.message && !!message.message.message){
              this.openDialog(message.message);
              localStorage.setItem('messageShown',"true");
            }
          });

      });
  }

  public getRecommendationEvent() {
    this._recommendationService.getMarkAsCompletedEvent().subscribe((res: any) => {
      if (res) {
        this.recommendationsCompletedForUser$.next([]);
        this._recommendationService.loadCompletedRecommendationsForUser(this.loggedInUser.id)
          .pipe(
            takeUntil(this.destroy$))
          .subscribe((recommendationsData: RecommendationInterface[]) => {
            let recommendation: RecommendationInterface = undefined;
            const recommendations: RecommendationInterface[] = [];
            for (const key in recommendationsData) {
              // skip loop if the property is from prototype
              if (!recommendationsData.hasOwnProperty(key)) { continue; }

              recommendation = recommendationsData[key];
              if (recommendation.isComplete && recommendation.subject) {
                recommendations.push(recommendation);
              }
            }
            this.recommendationsCompletedForUser$.next(recommendations);
          });
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public openTaskComparator(task1: TaskInterface, task2: TaskInterface): number {
    const time1: number = task1.dueDate ? new Date(task1.dueDate).getTime() : 0;
    const time2: number = task2.dueDate ? new Date(task2.dueDate).getTime() : 0;
    if (time1 === time2) {
      const created1: number = task1.creationDate ? new Date(task1.creationDate).getTime() : 0;
      const created2: number = task2.creationDate ? new Date(task2.creationDate).getTime() : 0;

      return created1 - created2;
    }

    return time1 - time2;
  }

  public closedTaskComparator(task1: TaskInterface, task2: TaskInterface): number {
    const time1: number = task1.completedDate ? new Date(task1.completedDate).getTime() : 0;
    const time2: number = task2.completedDate ? new Date(task2.completedDate).getTime() : 0;
    if (time1 === time2) {
      const due1: number = task1.id;
      const due2: number = task2.id;

      return due2 - due1;
    }

    return time2 - time1;
  }

  public openHelpDialog(): void {
    this.dialog.open(MyZoneHelpDialogComponent, {
      height: '700px',
      width: '700px',
      panelClass: 'my-zone-help-dialog',
    });
  }
  public closeSnackBar(): void {
      this._snackBar.dismiss();
  }
}
